import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import AgentLive, { StepsFold } from './AgentLive';

// The chat of one card: people, imported quotes (marked as such — nobody sent
// them from the app) and the Autoexecutor. "@autoexecutor …" is an
// instruction to it; "@name" addresses a colleague. The picker lists the
// editor's accounts plus the executor.

const EXECUTOR = 'autoexecutor';
const fmtTime = (s) => (s ? new Date(s).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '');

// @mentions as chips inside any text node of the rendered markdown.
function withMentions(children) {
  return React.Children.map(children, (c) => {
    if (typeof c !== 'string') return c;
    return c.split(/(@[\w.-]+)/g).filter(Boolean).map((p, j) => (/^@[\w.-]+$/.test(p)
      ? <span key={j} className={`tk-mention ${p.slice(1).toLowerCase() === EXECUTOR ? 'exec' : ''}`}>{p}</span>
      : p));
  });
}
const MD_COMPONENTS = {
  p: ({ children }) => <p className="tk-md-p">{withMentions(children)}</p>,
  li: ({ children }) => <li>{withMentions(children)}</li>,
  td: ({ children }) => <td>{withMentions(children)}</td>,
  th: ({ children }) => <th>{withMentions(children)}</th>,
  a: ({ href, children }) => <a href={href} target="_blank" rel="noreferrer">{children}</a>,
  table: ({ children }) => <div className="tk-md-table-wrap"><table className="tk-md-table">{children}</table></div>,
};

// The Autoexecutor and the history lines write markdown (lists, tables).
// People and imported quotes write plain text, shown exactly as typed: a
// hand-numbered list «1) … 2-3) … 4) …» is not a markdown list (markdown
// would renumber it 1, 2, 3 and swallow the «2-3)» line), stars around a word
// are stars. Line breaks stay (pre-wrap), as in a chat; @mentions become chips.
const PLAIN_KINDS = ['human', 'import'];
function renderBody(body, authorKind) {
  const text = String(body || '');
  if (PLAIN_KINDS.includes(authorKind)) return <div className="tk-msg-plain">{withMentions([text])}</div>;
  return (
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_COMPONENTS} skipHtml>
      {text}
    </ReactMarkdown>
  );
}

// A new message starts addressed to the Autoexecutor; the person erases the
// address to write to people only. A draft (an erased address included) is
// kept per card for the session.
const ADDRESS = `@${EXECUTOR} `;
const draftKey = (id) => `tk:draft:${id}`;
const readDraft = (id, agent) => { try { const v = id ? window.sessionStorage.getItem(draftKey(id)) : null; return v == null || (!agent && v === ADDRESS) ? (agent ? ADDRESS : '') : v; } catch (e) { return agent ? ADDRESS : ''; } };
const writeDraft = (id, v) => { try { if (!id) return; if (v === ADDRESS) window.sessionStorage.removeItem(draftKey(id)); else window.sessionStorage.setItem(draftKey(id), v); } catch (e) { /* storage unavailable */ } };

// `highlightTaskId` — a message with `_tag` (written in another chat and shown
// here in one timeline, Dialog.js) is marked with where it came from; one of
// this card is lit. `compact` — a short list (a turn's chat inside a card).
export default function TaskChat({ taskId, messages, usernames, currentUser, onSend, busy, onOpenRun, renderAttachment, highlightTaskId = null, compact = false, agent = true }) {
  const [text, setTextState] = useState(() => readDraft(taskId, agent));
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState(null);
  // A message just sent is shown at once, from the server's reply to the
  // send, until the list read from the server has it: the full read of a chat
  // takes seconds while the agent's turn starts, and the agent's live turn
  // (fed by the stream) would otherwise appear below the previous reply, as
  // if answering it (23.10.2026).
  const [sent, setSent] = useState([]);
  useEffect(() => { setSent([]); }, [taskId]);
  useEffect(() => { setSent((prev) => (prev.length && prev.some((x) => messages.some((m) => m.id === x.id)) ? prev.filter((x) => !messages.some((m) => m.id === x.id)) : prev)); }, [messages]);
  const shown = sent.length ? messages.concat(sent.filter((x) => !messages.some((m) => m.id === x.id))) : messages;
  const setText = (v) => { setTextState(v); writeDraft(taskId, v); };
  const [pickerOpen, setPickerOpen] = useState(false);
  const areaRef = useRef(null);
  const listRef = useRef(null);
  const toEnd = () => { const el = listRef.current; if (el) el.scrollTop = el.scrollHeight; };
  // The chat scrolls on its own: it opens at the latest message and keeps to
  // the bottom as messages arrive, unless the person has scrolled up to read.
  const stickRef = useRef(true);
  const onListScroll = () => { const el = listRef.current; if (el) stickRef.current = el.scrollTop + el.clientHeight >= el.scrollHeight - 24; };
  useEffect(() => { stickRef.current = true; }, [taskId]);
  useEffect(() => { if (stickRef.current) requestAnimationFrame(toEnd); }, [shown]);
  // The agent's live turn grows at the bottom too (AgentLive).
  const onLiveGrow = useCallback(() => { if (stickRef.current) requestAnimationFrame(toEnd); }, []);
  const people = useMemo(() => [...(agent ? [EXECUTOR] : []), ...(usernames || []).filter((u) => u !== EXECUTOR)], [usernames, agent]);

  const insertMention = (name) => {
    const el = areaRef.current;
    const at = el ? el.selectionStart : text.length;
    const next = `${text.slice(0, at)}@${name} ${text.slice(at)}`;
    setText(next);
    setPickerOpen(false);
    setTimeout(() => { if (el) { el.focus(); el.selectionStart = el.selectionEnd = at + name.length + 2; } }, 0);
  };
  // The text is cleared only once the server has the message. On any failure
  // it stays in the box and the reason is shown right here, not somewhere up
  // the card.
  const send = async () => {
    const body = text.trim();
    if (!body || body === ADDRESS.trim() || busy || sending) return;
    setSending(true); setSendError(null);
    try {
      const r = await onSend(body);
      if (r && r.id) setSent((prev) => [...prev, r]);
      setText(agent ? ADDRESS : '');
      stickRef.current = true; setTimeout(toEnd, 50);
    } catch (e) {
      setSendError(e.message || 'Could not send the message');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className={`tk-chat ${compact ? 'compact' : ''}`}>
      <div className="tk-chat-list" ref={listRef} onScroll={onListScroll}>
        {shown.length === 0 ? <div className="tk-empty">{agent ? 'Describe what you want to change or ask the turn agent.' : 'Shared notes for the turn agents. Saving a comment does not start an agent.'}</div> : null}
        {shown.map((m) => (
          <div key={m.id} className={`tk-msg kind-${m.author_kind} ${m.meta && m.meta.kind ? `mk-${m.meta.kind}` : ''} ${m.author_kind === 'system' ? 'side-center' : (m.author_kind === 'human' && m.author === currentUser ? 'side-mine' : 'side-theirs')} ${m._tag ? 'from-elsewhere' : ''} ${highlightTaskId && m.task_id === highlightTaskId && m._tag ? 'own-card' : ''}`}>
            <div className="tk-msg-head">
              {m._tag ? <span className="tk-tag tk-tag-origin" title="Message from a related discussion, shown here for context">{m._tag}</span> : null}
              {m.author_kind === 'import' ? (
                <><span className="tk-tag tk-tag-import">imported quote</span> <span className="tk-author">{m.imported_author || 'unknown author'}</span>{m.imported_at ? <span className="tk-muted"> · said {fmtTime(m.imported_at)}</span> : null}</>
              ) : m.author_kind === 'autoexecutor' ? (
                <><span className="tk-tag tk-tag-exec">Autoexecutor</span>{m.meta && m.meta.attempt ? <span className="tk-muted"> · attempt {m.meta.attempt}</span> : null}</>
              ) : m.author_kind === 'system' ? (
                <span className="tk-tag tk-tag-system">event</span>
              ) : (
                <span className="tk-author">{m.author}</span>
              )}
              <span className="tk-muted tk-msg-time">{fmtTime(m.created_at)}</span>
              {m.meta && m.meta.run_id && onOpenRun ? <button type="button" className="tk-linkbtn" onClick={() => onOpenRun(m.meta.run_id)}>trace</button> : null}
            </div>
            {m.meta && m.meta.steps ? <StepsFold steps={m.meta.steps} /> : null}
            <div className="tk-msg-body">{renderBody(m.body, m.author_kind)}</div>
            {renderAttachment ? renderAttachment(m) : null}
          </div>
        ))}
        {taskId && agent ? <AgentLive taskId={taskId} messages={messages} onGrow={onLiveGrow} /> : null}
      </div>
      <div className="tk-chat-compose">
        <textarea
          ref={areaRef}
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') send(); }}
          placeholder={agent ? 'Ask the turn agent. «stop» stops the current attempt. Ctrl+Enter sends.' : 'A shared decision or comment for the remark document. No agent is started.'}
          rows={3}
          aria-label="Message"
        />
        <div className="tk-chat-actions">
          <div className="tk-picker-wrap">
            <button type="button" className="tk-btn tk-btn-ghost" onClick={() => setPickerOpen((v) => !v)} aria-label="Mention someone">@ mention</button>
            {pickerOpen ? (
              <div className="tk-picker" role="listbox">
                {people.map((u) => <button type="button" key={u} className="tk-picker-item" onClick={() => insertMention(u)}>@{u}{u === EXECUTOR ? ' — the executor' : (u === currentUser ? ' — you' : '')}</button>)}
              </div>
            ) : null}
          </div>
          {agent ? <button type="button" className="tk-btn tk-btn-ghost" onClick={() => insertMention(EXECUTOR)}>@autoexecutor</button> : null}
          <span className="tk-grow" />
          <button type="button" className="tk-btn tk-btn-primary" onClick={send} disabled={busy || sending || !text.trim()}>{sending ? 'Sending…' : 'Send'}</button>
        </div>
        {sendError ? <div className="tk-error" role="alert">Message not sent: {sendError}. Your text is still in the input; you can try sending it again.</div> : null}
      </div>
    </div>
  );
}
